import { Caption } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { fetchLiveCatalogRegistrationByIdIfNeeded } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveCatalogCurrentItemIndex } from '@/redux/modules/console';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { ItemCardComponent, ItemCardInfo } from '../../../types/types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import ItemCardAuctionName from '@/components/GridItemCard/Card/components/ItemCardAuctionName/ItemCardAuctionName';
import ItemCardImage from '../../components/ItemCardImage/ItemCardImage';
import ItemCardMenu from '@/components/ItemCard/components/Menu/ItemCardMenu';
import ItemCardPriceRow from '../../components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardShortDate from '../../components/ItemCardShortDate/ItemCardShortDate';
import ItemCardTitle from '../../components/ItemCardTitle/ItemCardTitle';
import loadable from '@loadable/component';
import ms from 'ms';
import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';

/** Establish connection to real-time auction events, only on the client-side */
const LiveAuctionEventsSubscriber = loadable(
    () => import('@/components/LiveEventSubscribers/LiveAuctionEventsSubscriber'),
    {
        ssr: false,
    }
);

const FollowedSearchItemPageItemCard: ItemCardComponent = ({ className, itemId }) => {
    const dispatch = useAppDispatch();

    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const [followedSearchItemCardHovered, setFollowedSearchItemCardHovered] = React.useState(false);
    const {
        bidCount,
        buyNowStatus,
        catalogId,
        catalogStatus,
        index,
        isLocked,
        isPassed,
        isSold,
        saleStartTs,
        sellerId,
    } = useAppSelector((state) => getItemSummary(state, itemId));

    // Logic for when an auction is live
    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;

    const previousCatalogStatus = usePrevious(catalogStatus);

    React.useEffect(() => {
        if (previousCatalogStatus !== 'live' && catalogStatus === 'live') {
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));
            dispatch(fetchCatalogPresence(catalogId));
            dispatch(
                fetchLiveCatalogRegistrationByIdIfNeeded({
                    catalogId,
                    sellerId,
                })
            );
        }
    }, [catalogId, catalogStatus, sellerId, dispatch, previousCatalogStatus]);

    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();
    const isMobile = useIsNarrowerThan(tabletNarrowWidthNum + 1);

    const catalogLiveWithinAWeek = catalogStatus !== 'done' && Date.now() > saleStartTs * 1000 - ms('7d');
    const shouldNotShowAuctionName = (catalogStatus === 'live' || 'online') && isMobile && catalogLiveWithinAWeek;
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const biddingWithin7Days = Date.now() > saleStartTs * 1000 - ms('7d');
    const showDateWithoutYear = catalogStatus === 'online' && !biddingWithin7Days;
    const showDateWithOnlyYear = catalogStatus === 'done';
    const isBuyNowOnMobile = isMobile && Boolean(buyNowStatus);
    const showAuctionName = !shouldNotShowAuctionName || !isBuyNowOnMobile;
    const isPending = isLocked && !isPassed && !isSold && !isLotOpen;

    return (
        <FollowedSearchPageItemCardContainer
            className={className}
            data-testid="my-bids-item-card"
        >
            {comingUp && (
                <LiveAuctionEventsSubscriber
                    catalogId={catalogId}
                    limitMessagesFilter="itemCard"
                />
            )}
            <StyledImageContainer
                data-testid="image-container"
                onMouseEnter={() => setFollowedSearchItemCardHovered(true)}
                onMouseLeave={() => setFollowedSearchItemCardHovered(false)}
            >
                <StyledFavoriteButton>
                    <ItemCardSaveIcon
                        canShowEasyShipping
                        itemId={itemId}
                    />
                </StyledFavoriteButton>
                <ItemCardImage
                    itemId={itemId}
                    showBidStatus
                    useInFluidGrid
                />
            </StyledImageContainer>

            <StyledPlacedAuctionNameAndDate>
                <StyledItemCardShortDate
                    allowBidding
                    itemId={itemId}
                    showDateWithoutYear={showDateWithoutYear}
                    showOnlyYear={showDateWithOnlyYear}
                />
                {showAuctionName && <ItemCardAuctionName itemId={itemId} />}
            </StyledPlacedAuctionNameAndDate>

            <ItemCardTitle
                isHovered={followedSearchItemCardHovered}
                itemId={itemId}
                onMouseEnter={() => setFollowedSearchItemCardHovered(true)}
                onMouseLeave={() => setFollowedSearchItemCardHovered(false)}
            />

            <PlacedPriceInformation
                onMouseEnter={() => {
                    setFollowedSearchItemCardHovered(true);
                }}
                onMouseLeave={() => {
                    setFollowedSearchItemCardHovered(false);
                }}
            >
                <StyledBidInfo>
                    <ItemCardPriceRow
                        isHovered={followedSearchItemCardHovered}
                        itemId={itemId}
                    />
                    {!isSold && !isPassed && !isPending && bidCount > 0 && (
                        <StyledBidCount
                            $darkGrey
                            $isHovered={followedSearchItemCardHovered}
                            onMouseEnter={() => {
                                setFollowedSearchItemCardHovered(true);
                            }}
                            onMouseLeave={() => {
                                setFollowedSearchItemCardHovered(false);
                            }}
                        >
                            <FormattedMessage
                                id="itemBidding.bid"
                                values={{ bidCount }}
                            />
                        </StyledBidCount>
                    )}
                </StyledBidInfo>
                {isLoggedIn && (
                    <ItemCardMenu
                        itemId={itemId}
                        showIcon={followedSearchItemCardHovered}
                    />
                )}
            </PlacedPriceInformation>
        </FollowedSearchPageItemCardContainer>
    );
};

const info: ItemCardInfo = {
    ItemCardComponent: FollowedSearchItemPageItemCard,
};

export default info;

const FollowedSearchPageItemCardContainer = styled.div`
    position: relative;
    min-width: 0;
`;

const StyledImageContainer = styled.section`
    position: relative;
    aspect-ratio: 1 / 1;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StyledItemCardShortDate = styled(ItemCardShortDate)`
    margin-right: ${({ theme }) => theme.spacing.xs};
    flex-grow: 1;
`;

const StyledPlacedAuctionNameAndDate = styled.span`
    display: flex;
    justify-content: space-between;
    white-space: nowrap;
    margin-bottom: ${({ theme }) => theme.spacing.xxs};
`;

const PlacedPriceInformation = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StyledFavoriteButton = styled.span`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
`;

const StyledBidCount = styled(Caption)<{ $isHovered: boolean }>`
    margin-left: 4px;
    ${({ $isHovered }) =>
        $isHovered &&
        css`
            color: ${({ theme }) => theme.colors.blue200};
            text-decoration: underline;
        `}
`;

const StyledBidInfo = styled.div`
    display: flex;
    align-items: center;
`;
